import React from 'react';
import { Search, Mail, Sparkles, Briefcase, Headphones, Bot, Plus, MoreHorizontal } from 'lucide-react';
import { motion } from 'framer-motion';
import { AgentTemplate } from './types';
const PREBUILT_AGENTS: AgentTemplate[] = [{
  id: '1',
  name: 'Email Assistant',
  description: 'Drafts replies & categorizes inbox',
  runs: '12.4k',
  icon: 'Mail',
  color: 'text-rose-400 bg-rose-400/10'
}, {
  id: '2',
  name: 'Lead Researcher',
  description: 'Enriches CRM contacts via web',
  runs: '8.2k',
  icon: 'Briefcase',
  color: 'text-blue-400 bg-blue-400/10'
}, {
  id: '3',
  name: 'Daily Briefing',
  description: 'Summarizes news & calendar',
  runs: '5.1k',
  icon: 'Sparkles',
  color: 'text-amber-400 bg-amber-400/10'
}, {
  id: '4',
  name: 'Support Bot',
  description: 'Auto-resolves Tier 1 tickets',
  runs: '45.8k',
  icon: 'Headphones',
  color: 'text-emerald-400 bg-emerald-400/10'
}];
const YOUR_AGENTS: AgentTemplate[] = [{
  id: '5',
  name: 'Morning Email Agent',
  description: 'Active workflow',
  runs: '142',
  icon: 'Bot',
  color: 'text-sky-400 bg-sky-400/10'
}, {
  id: '6',
  name: 'Invoice Parser',
  description: 'Extracts PDF data to Sheets',
  runs: '89',
  icon: 'Bot',
  color: 'text-purple-400 bg-purple-400/10'
}];
const IconMap: Record<string, React.ElementType> = {
  Mail,
  Briefcase,
  Sparkles,
  Headphones,
  Bot
};
export function LeftSidebar() {
  return <aside className="w-[280px] h-full bg-[#0b101d] border-r border-slate-800/60 flex flex-col shrink-0" data-id="element-32">
      <div className="p-4 border-b border-slate-800/60" data-id="element-33">
        <h2 className="text-sm font-semibold text-slate-200 mb-3" data-id="element-34">
          Agent Library
        </h2>
        <div className="relative" data-id="element-35">
          <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-500" data-id="element-36" />
          <input type="text" placeholder="Search templates..." className="w-full bg-slate-900 border border-slate-800 rounded-md py-1.5 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50 transition-all" data-id="element-37" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-6" data-id="element-38">
        {/* Pre-built Section */}
        <div data-id="element-39">
          <div className="flex items-center justify-between px-1 mb-2" data-id="element-40">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-41">
              Pre-built
            </span>
          </div>
          <div className="space-y-1.5" data-id="element-42">
            {PREBUILT_AGENTS.map(agent => {
            const Icon = IconMap[agent.icon];
            return <motion.div key={agent.id} whileHover={{
              scale: 1.02,
              backgroundColor: 'rgba(30, 41, 59, 0.5)'
            }} className="p-2.5 rounded-lg border border-transparent hover:border-slate-800 cursor-pointer group transition-colors" data-id="element-43">
                  <div className="flex items-start gap-3" data-id="element-44">
                    <div className={`p-2 rounded-md ${agent.color}`} data-id="element-45">
                      <Icon className="w-4 h-4" data-id="element-46" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-47">
                      <h3 className="text-sm font-medium text-slate-200 truncate" data-id="element-48">
                        {agent.name}
                      </h3>
                      <p className="text-xs text-slate-500 truncate mt-0.5" data-id="element-49">
                        {agent.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between pl-11" data-id="element-50">
                    <span className="text-[10px] font-medium text-slate-600 bg-slate-800/50 px-1.5 py-0.5 rounded" data-id="element-51">
                      {agent.runs} runs
                    </span>
                    <Plus className="w-3.5 h-3.5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-52" />
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Your Agents Section */}
        <div data-id="element-53">
          <div className="flex items-center justify-between px-1 mb-2" data-id="element-54">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-55">
              Your Agents
            </span>
            <button className="text-sky-400 hover:text-sky-300 p-1 rounded hover:bg-sky-400/10 transition-colors" data-id="element-56">
              <Plus className="w-3.5 h-3.5" data-id="element-57" />
            </button>
          </div>
          <div className="space-y-1.5" data-id="element-58">
            {YOUR_AGENTS.map(agent => {
            const Icon = IconMap[agent.icon];
            const isActive = agent.id === '5';
            return <div key={agent.id} className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${isActive ? 'bg-slate-800/50 border-slate-700' : 'border-transparent hover:bg-slate-800/30 hover:border-slate-800'}`} data-id="element-59">
                  <div className="flex items-center justify-between" data-id="element-60">
                    <div className="flex items-center gap-2.5" data-id="element-61">
                      <div className={`p-1.5 rounded-md ${agent.color}`} data-id="element-62">
                        <Icon className="w-3.5 h-3.5" data-id="element-63" />
                      </div>
                      <div data-id="element-64">
                        <h3 className={`text-sm font-medium ${isActive ? 'text-sky-400' : 'text-slate-200'}`} data-id="element-65">
                          {agent.name}
                        </h3>
                      </div>
                    </div>
                    <MoreHorizontal className="w-4 h-4 text-slate-600 hover:text-slate-300" data-id="element-66" />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </aside>;
}